import styled from "styled-components";
import { theme } from "styles/theme";

export const ViewTabs = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ViewTab = styled.button<{ $active: boolean }>`
  appearance: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: ${theme.fontSizes.regular};
  font-weight: ${theme.fontWeights.bold};
  padding: 0.7rem 1.15rem;
  border-radius: ${theme.borderRadius.small};
  background: ${({ $active }) => ($active ? theme.colors.primary : theme.colors.bgLight)};
  color: ${({ $active }) => ($active ? theme.colors.onPrimary : theme.colors.textPrimary)};

  &:focus-visible {
    outline: 2px solid ${theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
`;

export const Bench = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 0.85fr) minmax(0, 1.35fr);
  gap: 1.25rem;
  align-items: stretch;

  @media (max-width: ${theme.breakpoints.laptop}) {
    grid-template-columns: 1fr;
  }
`;

export const ConnectBand = styled.section`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.medium};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1.5rem;
`;

export const UnitStage = styled.section`
  background: ${theme.colors.bgLight};
  border-radius: ${theme.borderRadius.medium};
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const HeroSize = styled.p`
  margin: 0;
  font-size: 2.25rem;
  font-weight: ${theme.fontWeights.bold};
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: ${theme.colors.textPrimary};
`;

export const SerialLine = styled.p`
  margin: 0.35rem 0 0;
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.textPrimary};
`;

export const FactLine = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.regular};
  line-height: 1.5;
  color: ${theme.colors.textPrimary};
`;

export const FieldLabel = styled.span`
  color: ${theme.colors.textSecondary};
`;

export const RuntimeBand = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 1rem 1.5rem;
  padding: 1.25rem 1.5rem;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.medium};
`;

export const RuntimeHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  grid-column: 1 / -1;

  h2 {
    margin: 0;
  }
`;

export const RuntimeFigure = styled.div`
  dt {
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.textSecondary};
  }
  dd {
    margin: 0.15rem 0 0;
    font-size: ${theme.fontSizes.large};
    font-weight: ${theme.fontWeights.bold};
    letter-spacing: -0.03em;
    color: ${theme.colors.textPrimary};
  }
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;

  @media (max-width: ${theme.breakpoints.laptop}) {
    grid-template-columns: 1fr;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

export const SizeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const SizeTile = styled.div`
  padding: 0.75rem 0.875rem;
  border-radius: ${theme.borderRadius.small};
  background: ${theme.colors.bgLight};
  border: 1px solid ${theme.colors.border};

  dt {
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.textSecondary};
  }

  dd {
    margin: 0.25rem 0 0;
    font-size: ${theme.fontSizes.medium};
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.textPrimary};
  }
`;

export const EmptyState = styled.p`
  margin: 0;
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;

export const Card = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.medium};
  padding: 1.25rem 1.5rem;
`;

export const CardTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: ${theme.fontSizes.medium};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.textPrimary};
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

export const StatusText = styled.p<{ $tone: "ok" | "busy" | "idle" | "error" }>`
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ $tone }) =>
    $tone === "ok"
      ? theme.colors.statusActiveText
      : $tone === "error"
        ? theme.colors.error
        : theme.colors.textSecondary};
  font-weight: ${theme.fontWeights.medium};
`;

export const Notice = styled.div<{ $tone: "info" | "warning" | "error" | "success" }>`
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.small};
  margin-top: 1rem;
  background: ${({ $tone }) =>
    $tone === "warning"
      ? theme.colors.warningBg
      : $tone === "error"
        ? theme.colors.errorBg
        : $tone === "success"
          ? theme.colors.successBg
          : theme.colors.infoLight};
  color: ${({ $tone }) => ($tone === "warning" ? theme.colors.warningText : theme.colors.textPrimary)};
`;

export const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1.25rem;
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const InfoItem = styled.div`
  dt {
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.textSecondary};
  }
  dd {
    margin: 0.125rem 0 0;
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.textPrimary};
    word-break: break-word;
  }
`;

export const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const AdminField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.regular};
  font-weight: ${theme.fontWeights.medium};

  .controls {
    display: flex;
    gap: 0.5rem;
    align-items: stretch;
  }

  select {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    font: inherit;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.bgLighter};
    border: 0;
    border-radius: ${theme.borderRadius.small};
    padding: 0.55rem 0.7rem;
  }
`;

export const UpgradeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.fontSizes.small};

  th,
  td {
    text-align: left;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid ${theme.colors.border};
    color: ${theme.colors.textPrimary};
  }

  th {
    color: ${theme.colors.textSecondary};
    font-weight: ${theme.fontWeights.medium};
  }
`;

export const LogToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;

  h2 {
    margin: 0;
  }
`;

export const LogList = styled.ol`
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
  max-height: 280px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const LogLine = styled.li<{ $kind: "sent" | "firmware" | "note" }>`
  display: grid;
  grid-template-columns: 5.75rem 6.25rem minmax(0, 1fr);
  gap: 0.75rem;
  align-items: baseline;
  padding: 0.45rem 0.7rem;
  border-radius: ${theme.borderRadius.small};
  background: ${({ $kind }) =>
    $kind === "sent"
      ? theme.colors.primaryTransparent
      : $kind === "firmware"
        ? theme.colors.tertiaryPale
        : theme.colors.bgLight};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 0.15rem;
  }
`;

export const LogMeta = styled.span`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
`;

export const LogKind = styled.span<{ $kind: "sent" | "firmware" | "note" }>`
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $kind }) =>
    $kind === "sent" ? theme.colors.primaryDark : $kind === "firmware" ? theme.colors.tertiary : theme.colors.textSecondary};
`;

export const LogText = styled.span`
  font-family: ${theme.fonts.code};
  font-size: 0.75rem;
  color: ${theme.colors.textPrimary};
  word-break: break-word;
`;
